import React, { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { Plus, X, Trash2, Upload, Wallpaper, Check } from "lucide-react";
import { toast } from "sonner";

/**
 * Compact library of wallpaper sets — the admin can group backdrops (e.g. Marvel-Set)
 * and later drop them onto new showtimes with one click.
 */
export function WallpaperPicker({ onPick, onClose }) {
  const [cols, setCols] = useState([]);
  const load = () => api.get("/wallpaper-collections").then((r) => setCols(r.data));
  useEffect(() => { load(); }, []);
  return (
    <div className="fixed inset-0 z-[70] bg-black/80 backdrop-blur-sm p-4 overflow-y-auto flex items-start justify-center">
      <div className="w-full max-w-3xl bg-[#0A0A0A] border border-[#2A2A2A] rounded-xl my-3 sm:my-6">
        <div className="flex items-center justify-between px-6 py-4 border-b border-[#1A1A1A]">
          <div className="font-heading text-2xl">Wallpaper-Bibliothek</div>
          <button onClick={onClose}><X /></button>
        </div>
        <div className="p-6 space-y-4">
          {cols.map((c) => (
            <div key={c.id}>
              <div className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37] mb-2">{c.name}</div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-2">
                {(c.images || []).map((u, i) => (
                  <button key={i} onClick={() => { onPick(u); onClose(); }}
                    data-testid={`wallpaper-pick-${c.name}-${i}`}
                    className="aspect-video bg-[#1A1A1A] rounded overflow-hidden border border-[#2A2A2A] hover:border-[#D4AF37] transitions-safe">
                    <img src={u} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
                  </button>
                ))}
                {!c.images?.length && <div className="text-xs text-[#A8A398]">Leer.</div>}
              </div>
            </div>
          ))}
          {!cols.length && <div className="text-center text-sm text-[#A8A398] py-8">Noch keine Sets. Lege in der Verwaltung Sets an (z. B. „Marvel-Set").</div>}
        </div>
      </div>
    </div>
  );
}

export default function WallpaperLibrary() {
  const [cols, setCols] = useState([]);
  const [busy, setBusy] = useState(false);
  const [newName, setNewName] = useState("");
  const load = () => api.get("/wallpaper-collections").then((r) => setCols(r.data));
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!newName.trim()) return;
    try { await api.post("/wallpaper-collections", { name: newName.trim(), images: [] }); toast.success("Set erstellt"); setNewName(""); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const remove = async (id) => { if (!window.confirm("Set löschen?")) return; await api.delete(`/wallpaper-collections/${id}`); load(); };
  const upload = async (c, file) => {
    setBusy(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const url = `${process.env.REACT_APP_BACKEND_URL}${data.url}`;
      await api.patch(`/wallpaper-collections/${c.id}`, { name: c.name, images: [...(c.images || []), url] });
      toast.success("Bild hinzugefügt"); load();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  const removeImg = async (c, i) => {
    await api.patch(`/wallpaper-collections/${c.id}`, { name: c.name, images: c.images.filter((_, idx) => idx !== i) });
    load();
  };

  return (
    <div className="space-y-6" data-testid="wallpaper-library-page">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <div className="text-[10px] uppercase tracking-[0.3em] text-[#A8A398]">Bibliothek</div>
          <h1 className="font-heading text-2xl sm:text-3xl md:text-4xl flex items-center gap-3"><Wallpaper size={30} className="text-[#D4AF37]" /> Wallpaper-Sets</h1>
        </div>
        <div className="flex gap-2 flex-wrap w-full sm:w-auto">
          <input value={newName} onChange={(e) => setNewName(e.target.value)} placeholder="Neues Set (z. B. Marvel-Set)"
            className="bg-[#050505] border border-[#2A2A2A] rounded px-3 py-2 text-sm outline-none focus:border-[#D4AF37] flex-1 sm:flex-none sm:w-64 min-w-[180px]" />
          <button onClick={create} className="bg-[#D4AF37] text-black px-4 py-2 rounded-md text-sm uppercase tracking-widest hover:bg-[#EEDC82] flex items-center gap-2 shrink-0"><Plus size={14} /> Set anlegen</button>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {cols.map((c) => (
          <div key={c.id} className="card-lux p-4">
            <div className="flex items-center justify-between gap-2 flex-wrap mb-3">
              <div className="font-heading text-xl">{c.name}</div>
              <button onClick={() => remove(c.id)} className="text-xs px-2 py-1 border border-[#2A2A2A] rounded hover:border-[#7A1921]"><Trash2 size={12} /></button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {(c.images || []).map((u, i) => (
                <div key={i} className="relative aspect-video bg-[#1A1A1A] rounded overflow-hidden group">
                  <img src={u} alt="" onError={(e) => { e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover" />
                  <button onClick={() => removeImg(c, i)} className="absolute top-1 right-1 bg-black/70 border border-[#5B1319] rounded px-1.5 py-0.5 text-[10px] text-[#EE9CA1] opacity-0 group-hover:opacity-100 transitions-safe">✕</button>
                </div>
              ))}
              <label className="aspect-video border border-dashed border-[#2A2A2A] rounded flex items-center justify-center text-xs text-[#A8A398] cursor-pointer hover:border-[#D4AF37] transitions-safe">
                <Upload size={14} /> <input type="file" accept="image/*" className="hidden" onChange={(e) => e.target.files?.[0] && upload(c, e.target.files[0])} />
              </label>
            </div>
          </div>
        ))}
        {!cols.length && <div className="text-[#A8A398] text-sm col-span-full">Noch keine Sets vorhanden.</div>}
      </div>
    </div>
  );
}
